import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { isFeatureEnabled } from '@/config/rollout';
import { toast } from 'sonner';
import apiErrorDetail from '@/lib/apiErrorDetail';
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  FlaskConical,
  ChevronRight,
  ClipboardCheck,
  FolderKanban,
  DollarSign,
  Settings,
  ShoppingCart,
  Upload,
  X,
} from 'lucide-react';

export default function DashboardPage({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [protocolTemplates, setProtocolTemplates] = useState([]);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    protocol_type: 'rich_text',
    protocol_subtype: '',
    editor_kind: 'rich_text',
    description: '',
    contentText: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    }
    setLoading(false);
  };

  const canManageProtocolTemplates = ['admin', 'manager'].includes(user.role);

  const loadProtocolTemplates = async () => {
    try {
      const res = await api.get('/protocol-templates');
      setProtocolTemplates(res.data);
    } catch (error) {
      toast.error('Failed to load protocol templates');
    }
  };

  const openTemplateManager = () => {
    setTemplateManagerOpen(true);
    loadProtocolTemplates();
  };

  const handleTemplateFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setTemplateForm(prev => ({
      ...prev,
      name: prev.name || file.name.replace(/\.[^.]+$/, ''),
      contentText: text,
    }));
  };

  const saveProtocolTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    setTemplateSaving(true);
    let parsedContent = {};
    if (templateForm.contentText.trim()) {
      try {
        parsedContent = JSON.parse(templateForm.contentText);
      } catch (error) {
        parsedContent = { body: templateForm.contentText };
      }
    }
    try {
      await api.post('/protocol-templates/upload', {
        name: templateForm.name.trim(),
        protocol_type: templateForm.protocol_type.trim() || 'rich_text',
        protocol_subtype: templateForm.protocol_subtype.trim(),
        editor_kind: templateForm.editor_kind,
        description: templateForm.description,
        content: parsedContent,
        schema: {},
        metadata: {},
      });
      toast.success('Protocol template saved');
      setTemplateForm({ name: '', protocol_type: 'rich_text', protocol_subtype: '', editor_kind: 'rich_text', description: '', contentText: '' });
      loadProtocolTemplates();
    } catch (error) {
      toast.error(apiErrorDetail(error.response?.data?.detail, 'Failed to save protocol template'));
    }
    setTemplateSaving(false);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  const displayName = user.name || user.full_name || user.email;
  const role = user.role || 'scientist';

  const focusAreasByRole = {
    admin: [
      { label: 'Admin Console', description: 'Manage users, SSO, and integrations.', icon: Settings, path: '/admin' },
      { label: 'Approval Queue', description: 'Review blocked requests and protocols.', icon: ClipboardCheck, path: '/approvals' },
      { label: 'Procurement', description: 'Track RFQs, quotes, purchase orders, and bills.', icon: ShoppingCart, path: '/procurement' },
    ],
    manager: [
      { label: 'Approval Queue', description: 'Review pending procurement and protocol work.', icon: ClipboardCheck, path: '/approvals' },
      { label: 'Timeline Planner', description: 'Check scheduled work and daily task summaries.', icon: FolderKanban, path: '/projects' },
      { label: 'Procurement', description: 'Track RFQs, quotes, purchase orders, and bills.', icon: ShoppingCart, path: '/procurement' },
    ],
    scientist: [
      { label: 'Procurement', description: 'Create and track lab supply requests.', icon: ShoppingCart, path: '/procurement' },
      { label: 'Timeline Planner', description: 'View scheduled work and daily task summaries.', icon: FolderKanban, path: '/projects' },
    ],
  };

  const focusAreas = [...(focusAreasByRole[role] || focusAreasByRole.scientist)];
  if (['admin', 'manager'].includes(role) && isFeatureEnabled('financials')) {
    focusAreas.push({
      label: 'Financials',
      description: 'Review project diary costs and experiment man hours.',
      icon: DollarSign,
      path: '/financials',
    });
  }

  // Clickable stat card component
  const StatCard = ({ icon: Icon, value, label, onClick, iconColor = 'text-primary', testId }) => (
    <button
      onClick={onClick}
      className="container-borderless p-6 w-full text-left hover:bg-accent/50 transition-colors group"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon size={20} className={iconColor} />
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold">{value}</span>
          <ChevronRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      <p className="text-sm font-medium">{label}</p>
    </button>
  );

  const FocusAction = ({ icon: Icon, label, description, path }) => (
    <button
      onClick={() => navigate(path)}
      className="w-full text-left flex items-start gap-3 rounded-sm border border-border p-4 hover:bg-accent/40 transition-colors"
    >
      <Icon size={18} className="mt-0.5 text-primary" />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground mt-1 leading-5">{description}</span>
      </span>
    </button>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-8">
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold heading mb-2" data-testid="page-title">Dashboard</h1>
            <p className="text-muted-foreground">Welcome back, {displayName}</p>
          </div>
          
          {canManageProtocolTemplates && isFeatureEnabled('protocols') && (
            <Button onClick={openTemplateManager} variant="outline">
              <Upload size={16} className="mr-2" />
              Protocol Templates
            </Button>
          )}
        </div>

        {/* Stats Grid - Now Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Clock}
            value={stats?.pending_approvals || 0}
            label="Pending Approvals"
            onClick={() => user.role === 'manager' ? navigate('/approvals') : navigate('/procurement')}
            testId="stat-pending-approvals"
          />

          {isFeatureEnabled('experimentTracking') && (
            <StatCard
              icon={FlaskConical}
              value={stats?.active_experiments || 0}
              label="Active Protocol Runs"
              onClick={() => navigate('/experiments')}
              testId="stat-active-experiments"
            />
          )}

          {isFeatureEnabled('inventory') && (
            <StatCard
              icon={AlertTriangle}
              value={stats?.low_stock_items || 0}
              label="Low Stock Alerts"
              onClick={() => navigate('/inventory')}
              iconColor="text-destructive"
              testId="stat-low-stock"
            />
          )}

          {isFeatureEnabled('protocols') && (
            <StatCard
              icon={CheckCircle}
              value={stats?.pending_protocols || 0}
              label="Protocol Templates Pending"
              onClick={() => navigate('/protocols')}
              testId="stat-pending-protocols"
            />
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          <div className="container-borderless p-6 xl:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-semibold">Focus Areas</h2>
                <p className="text-sm text-muted-foreground mt-1 capitalize">{role} workspace shortcuts</p>
              </div>
              <span className="text-xs rounded-full border border-border px-3 py-1 capitalize">{role}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {focusAreas.map((area) => (
                <FocusAction key={area.label} {...area} />
              ))}
            </div>
          </div>

          <div className="container-borderless p-6">
            <h2 className="font-semibold mb-4">Operational Snapshot</h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Requests needing attention</span>
                <span className="font-medium">{stats?.pending_approvals || 0}</span>
              </div>
              {isFeatureEnabled('inventory') && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Stock alerts</span>
                  <span className="font-medium">{stats?.low_stock_items || 0}</span>
                </div>
              )}
              {isFeatureEnabled('protocols') && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Draft protocol templates</span>
                  <span className="font-medium">{stats?.pending_protocols || 0}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="container-borderless p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold" data-testid="recent-requests-title">Recent Requests</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/procurement')}>
                View All <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
            {stats?.recent_requests?.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent requests</p>
            ) : (
              <div className="space-y-3">
                {stats?.recent_requests?.map(request => (
                  <button
                    key={request.id}
                    onClick={() => navigate('/procurement')}
                    className="w-full flex items-start justify-between py-2 border-b border-border last:border-0 hover:bg-accent/30 rounded px-2 -mx-2 transition-colors"
                  >
                    <div className="text-left">
                      <p className="text-sm font-medium">{request.item_name}</p>
                      <p className="text-xs text-muted-foreground">{request.scientist_name}</p>
                    </div>
                    <span className={`status-badge status-${request.status}`}>{request.status}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {isFeatureEnabled('protocols') && (
            <div className="container-borderless p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold" data-testid="recent-protocols-title">Recent Protocols</h2>
                <Button variant="ghost" size="sm" onClick={() => navigate('/protocols')}>
                  View All <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
              {stats?.recent_protocols?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recent protocols</p>
              ) : (
                <div className="space-y-3">
                  {stats?.recent_protocols?.map(protocol => (
                    <button
                      key={protocol.id}
                      onClick={() => navigate(`/protocols/${protocol.id}`)}
                      className="w-full flex items-start justify-between py-2 border-b border-border last:border-0 hover:bg-accent/30 rounded px-2 -mx-2 transition-colors"
                    >
                      <div className="text-left">
                        <p className="text-sm font-medium">{protocol.name}</p>
                        <p className="text-xs text-muted-foreground">{protocol.project_name}</p>
                      </div>
                      <span className={`status-badge status-${protocol.status}`}>{protocol.status}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {templateManagerOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-background border border-border rounded-md w-full max-w-4xl max-h-[86vh] overflow-auto">
              <div className="flex items-center justify-between border-b border-border p-4">
                <div>
                  <h2 className="font-semibold">Protocol Templates</h2>
                  <p className="text-xs text-muted-foreground mt-1">Manager/admin template library for future protocol runs.</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setTemplateManagerOpen(false)}>
                  <X size={16} />
                </Button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4 p-4">
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Existing Templates</h3>
                  <div className="space-y-2">
                    {protocolTemplates.length === 0 && <p className="text-sm text-muted-foreground">No templates found.</p>}
                    {protocolTemplates.map(template => (
                      <div key={template.id} className="rounded-sm border border-border p-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-medium">{template.name}</span>
                          <span className="text-xs rounded-full border border-border px-2 py-0.5">{template.editor_kind}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{template.protocol_type}{template.protocol_subtype ? ` · ${template.protocol_subtype}` : ''}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Upload / Create Template</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="text-sm">
                      <span className="block mb-1">Name</span>
                      <input className="w-full rounded-sm border border-border bg-background px-3 py-2" value={templateForm.name} onChange={e => setTemplateForm(prev => ({ ...prev, name: e.target.value }))} />
                    </label>
                    <label className="text-sm">
                      <span className="block mb-1">Editor Path</span>
                      <select className="w-full rounded-sm border border-border bg-background px-3 py-2" value={templateForm.editor_kind} onChange={e => setTemplateForm(prev => ({ ...prev, editor_kind: e.target.value }))}>
                        <option value="rich_text">Rich text SOP</option>
                        <option value="structured">Structured editor</option>
                      </select>
                    </label>
                    <label className="text-sm">
                      <span className="block mb-1">Protocol Type</span>
                      <input className="w-full rounded-sm border border-border bg-background px-3 py-2" value={templateForm.protocol_type} onChange={e => setTemplateForm(prev => ({ ...prev, protocol_type: e.target.value }))} placeholder="elisa, pcr, rich_text..." />
                    </label>
                    <label className="text-sm">
                      <span className="block mb-1">Subtype</span>
                      <input className="w-full rounded-sm border border-border bg-background px-3 py-2" value={templateForm.protocol_subtype} onChange={e => setTemplateForm(prev => ({ ...prev, protocol_subtype: e.target.value }))} placeholder="reporter, standard..." />
                    </label>
                  </div>
                  <label className="text-sm block">
                    <span className="block mb-1">Description</span>
                    <input className="w-full rounded-sm border border-border bg-background px-3 py-2" value={templateForm.description} onChange={e => setTemplateForm(prev => ({ ...prev, description: e.target.value }))} />
                  </label>
                  <label className="text-sm block">
                    <span className="block mb-1">Upload JSON/text template</span>
                    <input type="file" accept=".json,.txt,.md" onChange={handleTemplateFile} className="w-full text-sm" />
                  </label>
                  <label className="text-sm block">
                    <span className="block mb-1">Template Content</span>
                    <textarea className="w-full min-h-40 rounded-sm border border-border bg-background px-3 py-2 font-mono text-xs" value={templateForm.contentText} onChange={e => setTemplateForm(prev => ({ ...prev, contentText: e.target.value }))} placeholder='Paste JSON or rich text content. Non-JSON is stored as { "body": "..." }.' />
                  </label>
                  <div className="flex justify-end">
                    <Button onClick={saveProtocolTemplate} disabled={templateSaving}>
                      {templateSaving ? 'Saving...' : 'Save Template'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
